import React, { useState, useRef, useCallback } from 'react';
import { Box, Typography, Card, CardContent, LinearProgress } from '@mui/material';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';
import VideoPreview from './VideoPreview';
import { errorReporter } from '@/services/errorReportingService';

interface VideoFile {
  file: File;
  url: string;
  duration: number;
  name: string;
}

interface VideoUploadProps {
  onUploadComplete?: (result: { success: boolean; noteId?: string; filePath?: string; error?: string }) => void;
}

const VideoUpload: React.FC<VideoUploadProps> = ({ onUploadComplete }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoFile | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get user's plan features including video duration limit
  const planFeatures = useCurrentPlanFeatures();
  const planLimit = planFeatures.video_seconds; // This is already in seconds

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isValidFileType = (file: File): boolean => {
    return file.type === 'video/mp4' || file.type === 'video/webm';
  };

  const getVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        resolve(video.duration);
        URL.revokeObjectURL(video.src);
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const handleFiles = useCallback(async (files: FileList) => {
    const file = files[0];
    if (!file) return;

    if (!isValidFileType(file)) {
      alert('Please select an MP4 or WebM video file.');
      return;
    }

    setLoading(true);

    try {
      const duration = await getVideoDuration(file);
      const url = URL.createObjectURL(file);

      setSelectedVideo({
        file,
        url,
        duration,
        name: file.name,
      });
    } catch (error) {
      errorReporter.expected('Failed to process uploaded video', error, {
        feature: 'video',
        operation: 'processUpload',
      });
      alert('Error processing video file.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleClearVideo = () => {
    if (selectedVideo) {
      URL.revokeObjectURL(selectedVideo.url);
      setSelectedVideo(null);
    }
  };

  const planLimitFormatted = formatTime(planLimit);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          Your {planFeatures.planName} plan allows videos up to <strong>{planLimitFormatted}</strong>. Drag & drop an
          MP4 or WebM file to get started.
        </Typography>

        {!selectedVideo && (
          <Box
            sx={{
              border: dragActive ? '2px solid' : '2px dashed',
              borderColor: dragActive ? 'primary.main' : 'grey.300',
              backgroundColor: dragActive ? 'action.hover' : 'grey.50',
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              py: 6,
              textAlign: 'center',
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
          >
            <Typography variant="h6" sx={{ mb: 1, color: dragActive ? 'primary.main' : 'text.primary' }}>
              {dragActive ? 'Drop your video here' : 'Drag & Drop Video'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Or click to browse • MP4 or WebM files only
            </Typography>

            {loading && (
              <Box sx={{ mt: 2, maxWidth: 200, mx: 'auto' }}>
                <LinearProgress />
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  Processing video...
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="video/mp4,video/webm"
          style={{ display: 'none' }}
          onChange={handleInputChange}
        />

        {selectedVideo && (
          <Box sx={{ mt: 3 }}>
            <VideoPreview
              video={selectedVideo}
              planLimit={planLimit}
              onClear={handleClearVideo}
              onUploadComplete={onUploadComplete}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default VideoUpload;
